import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
} from "@material-ui/core";
import { css } from "@emotion/css";
import type { RestorePreviewEntity } from "../api";
import { useRestorePreview, useRestoreQuestionnaire } from "../queries";

const kindLabel = (e: RestorePreviewEntity): string => {
  switch (e.kind) {
    case "questionnaire":
      return "Structure & name";
    case "question":
      return `Question: ${e.label}`;
    case "question_group":
      return `Group: ${e.label}`;
  }
};

const RestoreDialog: React.VFC<{
  open: boolean;
  onClose: () => void;
  draftId: number;
  publishedId: number;
  snapshotLabel: string;
  onRestored?: () => void;
}> = ({ open, onClose, draftId, publishedId, snapshotLabel, onRestored }) => {
  const previewQuery = useRestorePreview(open ? draftId : null, publishedId);
  const restoreMutation = useRestoreQuestionnaire();

  const entities = previewQuery.data?.entities ?? [];

  const handleRestore = () => {
    restoreMutation.mutate(
      { id: draftId, publishedId },
      {
        onSuccess: () => {
          onClose();
          onRestored?.();
        },
      }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Restore draft from {snapshotLabel}</DialogTitle>
      <DialogContent dividers>
        {previewQuery.isLoading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        ) : previewQuery.error ? (
          <Typography color="error">Failed to load restore preview</Typography>
        ) : entities.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            The draft already matches this version — nothing to restore.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              These items will be rewritten to the content of this published version. Questions and groups are shared:
              restoring one also changes every other questionnaire using it.
            </Typography>
            <List dense>
              {entities.map(e => (
                <ListItem key={`${e.kind}:${e.source_id}`} className={styles.entityRow}>
                  <ListItemText
                    primary={
                      <span className={styles.entityLabel}>
                        {kindLabel(e)}
                        {e.status === "missing" && (
                          <Chip size="small" label="deleted — will be recreated" className={styles.missingChip} />
                        )}
                      </span>
                    }
                    secondary={
                      e.used_by.length > 0 ? `Also used by: ${e.used_by.map(u => u.name).join(", ")}` : undefined
                    }
                  />
                </ListItem>
              ))}
            </List>
            {restoreMutation.error ? <Typography color="error">Restore failed</Typography> : null}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleRestore}
          disabled={previewQuery.isLoading || entities.length === 0 || restoreMutation.isLoading}
        >
          {restoreMutation.isLoading ? "Restoring..." : "Restore"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RestoreDialog;

const styles = {
  entityRow: css`
    align-items: flex-start;
  `,
  entityLabel: css`
    display: inline-flex;
    align-items: center;
    gap: 8px;
  `,
  missingChip: css`
    background: #fff3e0;
    color: #b26a00;
  `,
};
